import { useContext, type ReactNode, type Ref } from "react";
import classNames from "classnames";
// eslint-disable-next-line no-restricted-imports -- the design-system wrapper around Base UI
import { Avatar as AvatarBase } from "@base-ui/react/avatar";
import { styleWithVars } from "@lib/cssVars";
import { AvatarGroupSizeContext } from "./avatarGroupContext";
import {
    hasAvatarSrcFailed,
    rememberFailedAvatarSrc,
} from "./failedAvatarSrcs";
import styles from "./Avatar.module.scss";

// Visual source of truth: docs/specs/2026-09-28-avatar-figma-rework.md,
// docs/specs/2026-09-29-entity-class-workspace-avatars.md and
// docs/specs/2026-10-23-colorless-people-avatars.md (tone).

/** xs 16px, sm 20px, md 24px, lg 32px. */
export type AvatarSize = "xs" | "sm" | "md" | "lg";
export type AvatarShape = "circle" | "square" | "diamond";
export type AvatarVariant = "solid" | "outline";
export type AvatarTone = "colorful" | "neutral";

interface AvatarCommonProps {
    /** Accessible name and the source of the initial. */
    name: string;
    /** Defaults to the enclosing AvatarGroup's size, else "sm". */
    size?: AvatarSize;
    /** `solid` fills the box; `outline` paints `--bg-primary` inside an inset ring. @default "solid" */
    variant?: AvatarVariant;
    /** Replaces the initial (Kick logo). */
    fallback?: ReactNode;
    /** Hides the avatar from assistive tech when a visible name sits next to it. */
    "aria-hidden"?: boolean;
    className?: string;
    ref?: Ref<HTMLSpanElement>;
}

/** Diamonds (classes) never show a photo. */
type AvatarShapeProps =
    | { shape?: "circle" | "square"; src?: string | null }
    | { shape: "diamond"; src?: never };

/** Only a `colorful` avatar takes a colour; `neutral` paints `--bg-tertiary` or a `--border-default` ring. */
type AvatarToneProps =
    | {
          tone?: "colorful";
          /** The fill (solid) or ring (outline), e.g. a user's or entity's `iconColor`. */
          color?: string;
      }
    | { tone: "neutral"; color?: never };

export type AvatarProps = AvatarCommonProps &
    AvatarShapeProps &
    AvatarToneProps;

const FALLBACK_DELAY_WITH_PHOTO_MS = 600;

const getInitial = (name: string) =>
    (Array.from(name.trim())[0] ?? "").toUpperCase();

/**
 * A person's, firm's, entity's, class's or bot's picture: the photo when `src`
 * loads, else the initial of `name` (or `fallback`). Presentational only;
 * product adapters map domain objects onto these props.
 *
 * Shapes: `circle` for people, the Kick bot and integration logos; `square`
 * (4px corner) for firm logos, entities, workspaces and consolidation groups;
 * `diamond` for classes. `variant` and `tone` are independent: `solid` or
 * `outline` paint, `colorful` (`color`, else ocean blue) or `neutral`. People,
 * consolidation groups and workspaces without a colour are `outline` +
 * `neutral`.
 * Marked `data-avatar-box`, so a Tag holding it pads to fit (see Tag).
 */
export const Avatar = ({
    name,
    src,
    color,
    size,
    shape = "circle",
    variant = "solid",
    tone = "colorful",
    fallback,
    "aria-hidden": isAriaHidden,
    className,
    ref,
}: Readonly<AvatarProps>) => {
    const groupSize = useContext(AvatarGroupSizeContext);
    const hasPhoto =
        src !== undefined &&
        src !== null &&
        src !== "" &&
        !hasAvatarSrcFailed(src);
    const accessibilityProps = isAriaHidden
        ? { "aria-hidden": true }
        : { role: "img", "aria-label": name };

    return (
        <AvatarBase.Root
            ref={ref}
            className={classNames(styles.avatar, className)}
            data-avatar-box=""
            data-size={size ?? groupSize ?? "sm"}
            data-shape={shape}
            data-variant={variant}
            data-tone={tone}
            style={
                color === undefined
                    ? undefined
                    : styleWithVars({ "--avatar-color": color })
            }
            {...accessibilityProps}
        >
            {hasPhoto && (
                <AvatarBase.Image
                    src={src}
                    alt=""
                    className={styles.image}
                    onLoadingStatusChange={(status) => {
                        if (status === "error") {
                            rememberFailedAvatarSrc(src);
                        }
                    }}
                />
            )}
            <AvatarBase.Fallback
                delay={hasPhoto ? FALLBACK_DELAY_WITH_PHOTO_MS : 0}
                aria-hidden
                // Opts the logo out of the svg recolouring that Button, Tag and
                // other controls apply to icons in their slots.
                className={classNames(styles.fallback, "icon-color-revert")}
            >
                {fallback ?? getInitial(name)}
            </AvatarBase.Fallback>
        </AvatarBase.Root>
    );
};
